// apps/store/src/components/chat/chat.api.ts
import { env } from '../../lib/env';
import type { CargoBranch } from '@/data/cargo.api';

export interface ChatButton {
  title: string;
  url?: string;
  payload?: string;
}

export interface ChatProduct {
  title: string;
  subtitle?: string;
  price?: number;
  imageUrl?: string;
  url: string;
  slug?: string;
  galleryImages?: string[];
  sizes?: string[];
}

export type ChatCargoBranch = CargoBranch;

export interface ChatPaymentBankUrl {
  name: string;
  description?: string;
  logo?: string;
  link: string;
}

export interface ChatPayment {
  orderId: string;
  invoiceId: string;
  amount: number;
  qrCode: string;
  qrCodeUrl?: string | null;
  expiresAt?: string | null;
  bankUrls: ChatPaymentBankUrl[];
}

export type ChatMessageBlock =
  | { type: 'summary'; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'meta'; text: string };

export interface ChatReply {
  text: string;
  conversationId?: string | null;
  blocks?: ChatMessageBlock[];
  buttons?: ChatButton[];
  products?: ChatProduct[];
  cargoBranches?: ChatCargoBranch[];
  payment?: ChatPayment;
  sources?: Array<{
    sourceId: string;
    chunkId: string;
    title: string;
    score: number;
  }>;
  suggestions?: string[];
}

export interface ChatRequestContext {
  lastShownProducts?: Array<{ slug: string; title: string; price?: number }>;
  selectedProductSlug?: string | null;
  checkoutState?: 'login_required_for_checkout' | 'shipping_required_for_checkout' | null;
}

export async function sendChatMessage(
  message: string,
  sessionId: string,
  accessToken?: string | null,
  context?: ChatRequestContext | null,
  clientRequestId?: string,
  conversationId?: string | null,
): Promise<ChatReply> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const payload = {
    message,
    sessionId,
    ...(conversationId ? { conversationId } : {}),
    ...(context ? { context } : {}),
    ...(clientRequestId ? { clientRequestId } : {}),
  };

  const res = await fetch(`${env.API_URL}/api/chat`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(payload),
  });

  if (!res.ok) throw new Error('Chat request failed');
  return res.json();
}

export type ChatStreamChunk =
  | { type: 'text-delta'; textDelta: string }
  | { type: 'tool-progress'; tool: string }
  | { type: 'tool-call'; tool: string }
  | { type: 'tool-result'; tool: string; ok: boolean }
  | { type: 'metadata'; metadata: Record<string, unknown> }
  | { type: 'final'; reply: ChatReply }
  | { type: 'error'; message: string };

export interface StreamChatCallbacks {
  onChunk: (chunk: ChatStreamChunk) => void;
  signal?: AbortSignal;
}

export async function streamChatMessage(
  message: string,
  sessionId: string,
  accessToken: string | null | undefined,
  callbacks: StreamChatCallbacks,
  context?: ChatRequestContext | null,
  clientRequestId?: string,
  conversationId?: string | null,
): Promise<void> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const payload = {
    message,
    sessionId,
    ...(conversationId ? { conversationId } : {}),
    ...(context ? { context } : {}),
    ...(clientRequestId ? { clientRequestId } : {}),
  };

  const res = await fetch(`${env.API_URL}/api/chat/stream`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(payload),
    signal: callbacks.signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`Stream chat failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let sepIdx: number;
    while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);
      const dataLine = rawEvent.split('\n').find((l) => l.startsWith('data:'));
      if (!dataLine) continue;
      const payload = dataLine.slice(5).trim();
      if (!payload) continue;
      try {
        callbacks.onChunk(JSON.parse(payload) as ChatStreamChunk);
      } catch {
        // skip malformed
      }
    }
  }
}

// ── Conversation History API ────────────────────────────────────

export interface ChatConversationSummary {
  id: string;
  title: string | null;
  summaryRedacted: string | null;
  status: string;
  retentionMode: string;
  lastMessageAt: string;
  startedAt: string;
}

export interface ChatHistoryMessage {
  id: string;
  turnIndex: number;
  role: 'USER' | 'ASSISTANT' | 'SYSTEM_EVENT';
  textRedacted: string;
  contentRedacted?: unknown;
  source: string | null;
  guardrailLabel: string | null;
  toolNames: string[];
  productRefs: unknown;
  createdAt: string;
}

function authHeaders(accessToken: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
}

export async function fetchConversations(
  accessToken: string,
  limit = 30,
  cursor?: string,
): Promise<ChatConversationSummary[]> {
  const params = new URLSearchParams();
  if (limit !== 30) params.set('limit', String(limit));
  if (cursor) params.set('cursor', cursor);
  const qs = params.toString();

  const res = await fetch(
    `${env.API_URL}/api/chat/conversations${qs ? `?${qs}` : ''}`,
    { headers: authHeaders(accessToken) },
  );
  if (!res.ok) throw new Error('Failed to load conversations');
  const data = await res.json();
  return data.conversations;
}

export async function fetchMessages(
  accessToken: string,
  conversationId: string,
  limit = 50,
): Promise<ChatHistoryMessage[]> {
  const params = limit !== 50 ? `?limit=${limit}` : '';
  const res = await fetch(
    `${env.API_URL}/api/chat/conversations/${conversationId}/messages${params}`,
    { headers: authHeaders(accessToken) },
  );
  if (!res.ok) throw new Error('Failed to load messages');
  const data = await res.json();
  return data.messages;
}

export async function deleteConversation(
  accessToken: string,
  conversationId: string,
): Promise<void> {
  const res = await fetch(
    `${env.API_URL}/api/chat/conversations/${conversationId}`,
    { method: 'DELETE', headers: authHeaders(accessToken) },
  );
  if (!res.ok) throw new Error('Failed to delete conversation');
}

export async function archiveConversation(
  accessToken: string,
  conversationId: string,
): Promise<void> {
  const res = await fetch(
    `${env.API_URL}/api/chat/conversations/${conversationId}`,
    {
      method: 'PATCH',
      headers: authHeaders(accessToken),
      body: JSON.stringify({ status: 'ARCHIVED' }),
    },
  );
  if (!res.ok) throw new Error('Failed to archive conversation');
}

export async function claimGuestConversation(
  accessToken: string,
  conversationId: string,
  guestSessionId: string,
): Promise<void> {
  const res = await fetch(
    `${env.API_URL}/api/chat/conversations/${conversationId}/claim`,
    {
      method: 'POST',
      headers: authHeaders(accessToken),
      body: JSON.stringify({ guestSessionId }),
    },
  );
  if (!res.ok) throw new Error('Failed to claim conversation');
}
